import { create } from "zustand";

export type Country = "DE" | "AT" | "CH" | "US" | "GB" | "NL" | "BE" | "PL" | "IT";
export type UnitSystem = "metric" | "imperial";
export type DemoLanguage = "de" | "en" | "en-gb" | "pl" | "it";

export interface LocaleConfig {
  country: Country;
  lang: string;
  currency: string;
  currencySymbol: string;
  decimalSep: string;
  thousandSep: string;
  unitSystem: UnitSystem;
  weightUnit: string;
  temperatureUnit: string;
}

export const LOCALES: Record<Country, LocaleConfig> = {
  DE: { country: "DE", lang: "de",    currency: "EUR", currencySymbol: "€",   decimalSep: ",", thousandSep: ".",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  AT: { country: "AT", lang: "de",    currency: "EUR", currencySymbol: "€",   decimalSep: ",", thousandSep: ".",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  CH: { country: "CH", lang: "de",    currency: "CHF", currencySymbol: "CHF", decimalSep: ".", thousandSep: "'",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  US: { country: "US", lang: "en",    currency: "USD", currencySymbol: "$",   decimalSep: ".", thousandSep: ",",  unitSystem: "imperial", weightUnit: "lbs", temperatureUnit: "°F" },
  GB: { country: "GB", lang: "en-gb", currency: "GBP", currencySymbol: "£",   decimalSep: ".", thousandSep: ",",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  NL: { country: "NL", lang: "nl",    currency: "EUR", currencySymbol: "€",   decimalSep: ",", thousandSep: ".",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  BE: { country: "BE", lang: "nl",    currency: "EUR", currencySymbol: "€",   decimalSep: ",", thousandSep: ".",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  PL: { country: "PL", lang: "pl",    currency: "PLN", currencySymbol: "zł",  decimalSep: ",", thousandSep: " ",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
  IT: { country: "IT", lang: "it",    currency: "EUR", currencySymbol: "€",   decimalSep: ",", thousandSep: ".",  unitSystem: "metric",   weightUnit: "kg",  temperatureUnit: "°C" },
};

export const DEMO_LANGUAGE_OPTIONS: Array<{ value: DemoLanguage; label: string }> = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English (US)" },
  { value: "en-gb", label: "English (UK)" },
  { value: "pl", label: "Polski" },
  { value: "it", label: "Italiano" },
];

const DEMO_LANGUAGE_COUNTRIES: Record<DemoLanguage, Country> = {
  de: "DE",
  en: "US",
  "en-gb": "GB",
  pl: "PL",
  it: "IT",
};

const DEMO_LANGUAGE_KEY = "alta_demo_lang";
const DEMO_COUNTRY_KEY = "alta_demo_country";

function getCountryForDemoLanguage(language: string | null | undefined): Country {
  return DEMO_LANGUAGE_COUNTRIES[language as DemoLanguage] ?? "DE";
}

function syncI18n(language: string): void {
  import("i18next").then(({ default: i18n }) => i18n.changeLanguage(language));
  import("../i18n").then(({ loadRemoteOverrides }) => loadRemoteOverrides(language)).catch(() => {});
}

export const KG_TO_LB = 2.20462;

interface LocaleStoreState {
  country: Country;
  /** Update locale locally + persist to API (best-effort). */
  setCountry: (country: Country) => void;
  /** Hydrate locale from user prefs API after authentication. */
  hydrateFromApi: () => Promise<void>;
  /** Apply a signed demo-link language without altering a real user's prefs. */
  setDemoLanguage: (language: string | null | undefined) => void;
  /** Restore the locally stored regular-user locale after leaving a demo. */
  clearDemoLanguage: () => void;
}

export const useLocaleStore = create<LocaleStoreState>((set) => ({
  country: (localStorage.getItem(DEMO_COUNTRY_KEY) as Country)
    ?? (localStorage.getItem("alta_country") as Country)
    ?? "DE",

  setCountry: (country) => {
    const config = LOCALES[country] ?? LOCALES.DE;
    // A demo has its own link-scoped locale. Do not overwrite the browser's
    // regular-user preference if this action is triggered outside the hidden
    // demo UI (for example by a future component).
    if (localStorage.getItem(DEMO_LANGUAGE_KEY)) {
      localStorage.setItem(DEMO_COUNTRY_KEY, config.country);
      localStorage.setItem(DEMO_LANGUAGE_KEY, config.lang);
      set({ country: config.country });
      syncI18n(config.lang);
      return;
    }
    localStorage.setItem("alta_country", country);
    localStorage.setItem("alta_lang", config.lang);
    set({ country });
    syncI18n(config.lang);
    // Best-effort API persistence — import lazily to avoid circular dep
    import("../lib/api")
      .then(({ api }) => api.put("/api/prefs/locale", { value: country }))
      .catch(() => {/* noop */});
  },

  hydrateFromApi: async () => {
    try {
      const { api } = await import("../lib/api");
      const { data } = await api.get<{ value: string | null }>("/api/prefs/locale");
      const raw = data?.value as Country | null | undefined;
      if (raw && LOCALES[raw]) {
        const config = LOCALES[raw];
        localStorage.setItem("alta_country", raw);
        localStorage.setItem("alta_lang", config.lang);
        set({ country: raw });
        // Sync i18next to the hydrated language
        const { default: i18n } = await import("i18next");
        if (i18n.language !== config.lang) {
          i18n.changeLanguage(config.lang);
        }
      }
    } catch {
      // Fall back to localStorage value silently
    }
  },

  setDemoLanguage: (language) => {
    const country = getCountryForDemoLanguage(language);
    const config = LOCALES[country];
    localStorage.setItem(DEMO_LANGUAGE_KEY, config.lang);
    localStorage.setItem(DEMO_COUNTRY_KEY, country);
    set({ country });
    syncI18n(config.lang);
  },

  clearDemoLanguage: () => {
    localStorage.removeItem(DEMO_LANGUAGE_KEY);
    localStorage.removeItem(DEMO_COUNTRY_KEY);
    const country = (localStorage.getItem("alta_country") as Country) ?? "DE";
    const config = LOCALES[country] ?? LOCALES.DE;
    set({ country: config.country });
    syncI18n(config.lang);
  },
}));

export function useLocale() {
  const country = useLocaleStore((s) => s.country);
  const locale = LOCALES[country] ?? LOCALES.DE;

  function fmtNumber(value: number, decimals = 1): string {
    const fixed = value.toFixed(decimals);
    const [intPart, fracPart] = fixed.split(".");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, locale.thousandSep);
    return decimals > 0 ? `${grouped}${locale.decimalSep}${fracPart}` : grouped;
  }

  function formatWeight(kg: number | null | undefined, decimals = 1): string {
    if (kg == null) return "—";
    const value =
      locale.unitSystem === "imperial" ? kg * KG_TO_LB : kg;
    return `${fmtNumber(value, decimals)} ${locale.weightUnit}`;
  }

  function formatTemp(celsius: number | null | undefined, decimals = 1): string {
    if (celsius == null) return "—";
    const value =
      locale.unitSystem === "imperial"
        ? (celsius * 9) / 5 + 32
        : celsius;
    return `${fmtNumber(value, decimals)} ${locale.temperatureUnit}`;
  }

  function formatNumber(
    value: number | null | undefined,
    decimals = 1,
    unit = "",
  ): string {
    if (value == null) return "—";
    return unit
      ? `${fmtNumber(value, decimals)} ${unit}`
      : fmtNumber(value, decimals);
  }

  function formatPercent(value: number | null | undefined, decimals = 1): string {
    if (value == null) return "—";
    return `${fmtNumber(value * 100, decimals)} %`;
  }

  return { locale, fmtNumber, formatWeight, formatTemp, formatNumber, formatPercent };
}
